import { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowLeft, ArrowRight, ChevronDown } from "@untitledui/icons"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import { estimatesApi } from "@/lib/api/estimates"
import { useEstimate as useEstimateQuery } from "@/lib/api/use-estimates"
import type {
  ApiEstimateLineItem,
  LineItemAction,
  VehicleComponent,
  VehicleSection,
  VehicleSide,
} from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import {
  ALL_SECTIONS,
  ACTION_OPTIONS,
  COMPONENT_OPTIONS,
  PART_TYPE_OPTIONS,
  SECTION_LABELS,
  SIDE_OPTIONS,
  componentLabel,
  sectionLabel,
} from "@/lib/estimates/catalog"
import {
  AddRowButton,
  ConfidenceFlag,
  Dropdown,
  EditedBadge,
  SectionHeader,
  SegmentedToggle,
  SuggestionMicrocopy,
} from "./atoms"
import { FE_DAMAGE_COPY } from "./copy"
import { runSectionsOrchestratorAndWait } from "./submit"
import { ConfirmDialog } from "../wizard-shared/confirm-dialog"
import { OrchestratorLoader } from "./orchestrator-loader"

// Full Estimate — Step 5 (Damage).
//
// Section-based editor. Each section is a card; components live
// inside. Add/edit/delete components per section. Add/remove
// sections. AI's verdict is the default content of every row;
// we surface explicit "Edited" badges + suggestion microcopy
// only when the user overrides.
//
// Saves all rows in one pass when the user advances. Doesn't
// trigger cost estimation here — that runs after operations
// (step 6) so cost can use both line items + operations.

interface StepDamageProps {
  estimateId: number
  onBack: () => void
  onNext: () => void
}

type PartType = "OEM" | "Aftermarket"

interface RowState {
  // null id = locally-created row, not yet on the server
  id: number | null
  // Stable local key so React keeps focus through edits
  localKey: string
  component: VehicleComponent
  section: VehicleSection | string
  // Server-only fields preserved across the editor session.
  // They're not exposed in the UI anymore (description + severity
  // were dropped to keep the form scannable) but still round-trip
  // through saves so the BE doesn't lose them.
  _description: string
  _severity: number
  action: LineItemAction
  side: VehicleSide
  partType: PartType
  confidence: string | null
  // Captured the first time we hydrate from the server; used to
  // drive the "Edited" badge + Suggested microcopy. Lives in
  // memory only — not re-hydrated across page loads.
  ai: {
    action: LineItemAction
    partType: PartType
  } | null
  // Server-side pricing snapshot — preserved across local edits
  // so the editor doesn't blow it away. Populated by the cost
  // agent after the user advances past operations.
  _min: number | null
  _max: number | null
  _avg: number | null
  _manHours: number
}

function lineItemToRow(li: ApiEstimateLineItem): RowState {
  const action: LineItemAction =
    li.action?.toUpperCase() === "REPLACE" ? "REPLACE" : "REPAIR"
  const partType: PartType =
    (li.partType ?? "").toLowerCase() === "aftermarket"
      ? "Aftermarket"
      : "OEM"
  return {
    id: li.id,
    localKey: `srv-${li.id}`,
    component: (li.component as VehicleComponent) ?? "FRONT_BUMPER_COVER",
    section: li.section ?? "MISCELLANEOUS",
    _description: li.description ?? "",
    _severity: li.severity ?? 5,
    action,
    side: li.side ?? "FRONT_CENTER",
    partType,
    confidence: li.confidence,
    ai: { action, partType },
    _min: li.min,
    _max: li.max,
    _avg: li.avg,
    _manHours: li.manHours ?? 0,
  }
}

let nextLocalRowId = 1

export function StepDamage({ estimateId, onBack, onNext }: StepDamageProps) {
  const [polling, setPolling] = useState(true)
  const { data: estimate } = useEstimateQuery(estimateId, {
    refetchInterval: polling ? 3000 : false,
  })

  const [rows, setRows] = useState<RowState[] | null>(null)
  // User-added empty sections (no rows yet). Stored separately so
  // the section card stays visible until a component is added.
  const [extraSections, setExtraSections] = useState<string[]>([])
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showSectionPicker, setShowSectionPicker] = useState(false)
  // Section pending removal — drives the in-app confirm dialog.
  // null when the dialog is closed; a section name when the user
  // tapped a trash icon and we're awaiting confirmation.
  const [sectionPendingRemoval, setSectionPendingRemoval] =
    useState<string | null>(null)
  // Component-row pending removal — same pattern as section. Holds
  // the row's localKey + display label so the dialog body can name
  // what's about to be deleted.
  const [rowPendingRemoval, setRowPendingRemoval] = useState<
    { key: string; label: string } | null
  >(null)

  // Hydrate from the first response that carries line items.
  useEffect(() => {
    if (!estimate) return
    if ((estimate.lineItems?.length ?? 0) > 0 && rows === null) {
      setRows(estimate.lineItems.map(lineItemToRow))
      setPolling(false)
    }
  }, [estimate, rows])

  // Group rows by section, preserving canonical order, then
  // appending any user-added sections that are still empty, then
  // any remaining sections (e.g. MISCELLANEOUS or unknown values).
  const sectionsInOrder = useMemo(() => {
    if (rows === null) return []
    const map = new Map<string, RowState[]>()
    for (const r of rows) {
      const key = String(r.section)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(r)
    }
    for (const s of extraSections) {
      if (!map.has(s)) map.set(s, [])
    }
    const ordered: Array<{ section: string; rows: RowState[] }> = []
    for (const s of ALL_SECTIONS) {
      if (map.has(s)) {
        ordered.push({ section: s, rows: map.get(s)! })
        map.delete(s)
      }
    }
    for (const [s, list] of map) {
      ordered.push({ section: s, rows: list })
    }
    return ordered
  }, [rows, extraSections])

  const usedSections = useMemo(() => {
    const set = new Set<string>()
    for (const s of sectionsInOrder) set.add(s.section)
    return set
  }, [sectionsInOrder])

  const updateRow = (key: string, patch: Partial<RowState>) => {
    setRows((curr) =>
      curr === null ? curr : curr.map((r) => (r.localKey === key ? { ...r, ...patch } : r)),
    )
  }

  // Open the confirm dialog. Caller passes a display label so the
  // dialog can name the component the user is about to delete.
  const removeRow = (key: string, label: string) => {
    setRowPendingRemoval({ key, label })
  }

  const confirmRemoveRow = () => {
    const target = rowPendingRemoval
    if (!target) return
    setRows((curr) =>
      curr === null ? curr : curr.filter((r) => r.localKey !== target.key),
    )
    if (editingKey === target.key) setEditingKey(null)
    setRowPendingRemoval(null)
  }

  const addRowToSection = (section: VehicleSection | string) => {
    if (rows === null) return
    const matching = COMPONENT_OPTIONS.find((c) => c.section === section)
    const newRow: RowState = {
      id: null,
      localKey: `local-${nextLocalRowId++}`,
      component: matching?.value ?? "FRONT_BUMPER_COVER",
      section,
      _description: "",
      _severity: 4,
      action: "REPAIR",
      side: "FRONT_CENTER",
      partType: "OEM",
      confidence: null,
      ai: null,
      _min: 0,
      _max: 0,
      _avg: 0,
      _manHours: 0,
    }
    setRows([...rows, newRow])
    setEditingKey(newRow.localKey)
    // If this is a previously-empty extra section, drop it from
    // extras now that it has a row.
    if (extraSections.includes(String(section))) {
      setExtraSections((s) => s.filter((x) => x !== String(section)))
    }
  }

  // Open the in-app confirm dialog. Actual removal happens in
  // confirmRemoveSection() once the user confirms.
  const removeSection = (section: string) => {
    setSectionPendingRemoval(section)
  }

  const confirmRemoveSection = () => {
    const section = sectionPendingRemoval
    if (!section) return
    setRows((curr) =>
      curr === null ? curr : curr.filter((r) => String(r.section) !== section),
    )
    setExtraSections((s) => s.filter((x) => x !== section))
    setSectionPendingRemoval(null)
  }

  const addNewSection = (section: VehicleSection) => {
    if (!usedSections.has(section)) {
      setExtraSections((s) => [...s, section])
    }
    setShowSectionPicker(false)
  }

  const handleSaveAndContinue = async () => {
    if (rows === null) return
    setSaving(true)
    try {
      // Batched PUT: all local edits land in one network call. BE
      // derives `section` from `component` server-side (vehicle-
      // section-from-component.ts) so we omit it from the payload.
      // Manual rows that the AI never touched have no severity —
      // default to 5 (medium) so the BE's required-int passes.
      await estimatesApi.updateLineItems(estimateId, {
        lineItems: rows.map((r) => ({
          id: r.id ?? undefined,
          component: r.component,
          description: r._description || undefined,
          action: r.action,
          side: r.side,
          partType: r.partType,
          severity: r._severity ?? 5,
        })),
      })
      // Then re-derive line items + operations + pricing. cost is
      // NOT called — orchestrator covers what cost would.
      await runSectionsOrchestratorAndWait(estimateId)
      onNext()
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't save"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  /* ── Loading state ────────────────────────────────────── */
  if (rows === null) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-10">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="size-10 border-[3px] border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
          <p className="text-[13.5px] text-zinc-500">
            {FE_DAMAGE_COPY.loading}
          </p>
        </div>
      </div>
    )
  }

  /* ── Orchestrator loader (Next pressed → orchestrator running) ── */
  // While `saving` is true the user shouldn't see the editor — show
  // the orchestrator's stages 4-5 instead, continuing the choreography
  // that started on the Generate screen.
  if (saving) {
    return <OrchestratorLoader done={false} />
  }

  const totalRows = rows.length
  const reviewCount = rows.filter(
    (r) => (r.confidence ?? "").toLowerCase() !== "high" && r.ai !== null,
  ).length

  return (
    <div className="space-y-5 pb-36 sm:pb-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2">
          {FE_DAMAGE_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {FE_DAMAGE_COPY.title}
        </h2>
        <p className="mt-1.5 text-[13.5px] text-zinc-500 leading-relaxed">
          {FE_DAMAGE_COPY.subtitle}
        </p>
      </header>

      {/* Summary bar — total components + review count */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white border border-zinc-200">
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="font-semibold text-zinc-900">
            {totalRows} {totalRows === 1 ? "component" : "components"}
          </span>
          <span className="text-zinc-300">·</span>
          <span className="text-zinc-500">
            {sectionsInOrder.length} {sectionsInOrder.length === 1 ? "section" : "sections"}
          </span>
        </div>
        {reviewCount > 0 && (
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-amber-700">
            <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
            {reviewCount} to review
          </span>
        )}
      </div>

      {/* Section cards */}
      <div className="space-y-4">
        <AnimatePresence initial={false}>
          {sectionsInOrder.map(({ section, rows: sectionRows }) => (
            <motion.div
              key={section}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
              className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
            >
              <SectionHeader
                title={sectionLabel(section)}
                count={sectionRows.length}
                noun="component"
                onRemove={() => removeSection(section)}
                removeAriaLabel={`Remove ${sectionLabel(section)}`}
              />
              <div className="divide-y divide-zinc-100">
                {sectionRows.map((row) => (
                  <ComponentRow
                    key={row.localKey}
                    row={row}
                    isEditing={editingKey === row.localKey}
                    onOpen={() => setEditingKey(row.localKey)}
                    onClose={() => setEditingKey(null)}
                    onUpdate={(patch) => updateRow(row.localKey, patch)}
                    onDelete={() =>
                      removeRow(row.localKey, componentLabel(row.component))
                    }
                  />
                ))}
                {sectionRows.length === 0 && (
                  <div className="px-4 md:px-5 py-3 text-[12.5px] text-zinc-400">
                    {FE_DAMAGE_COPY.emptySection}
                  </div>
                )}
              </div>
              <div className="px-4 md:px-5 py-3 bg-zinc-50/40 border-t border-zinc-100">
                <AddRowButton
                  label={FE_DAMAGE_COPY.addComponent}
                  onClick={() => addRowToSection(section)}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Add section dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSectionPicker((s) => !s)}
            className={cx(
              "w-full h-12 rounded-xl",
              "border-2 border-dashed border-zinc-300 hover:border-emerald-400",
              "bg-white hover:bg-emerald-50/40 transition-colors duration-150",
              "flex items-center justify-center gap-2",
              "text-[13px] font-semibold text-zinc-600 hover:text-emerald-800",
            )}
          >
            <svg
              viewBox="0 0 16 16"
              className="size-3.5"
              aria-hidden
              fill="none"
              strokeWidth="2.2"
              stroke="currentColor"
              strokeLinecap="round"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            {FE_DAMAGE_COPY.addSection}
            <ChevronDown
              className={cx(
                "size-3.5 transition-transform duration-150",
                showSectionPicker && "rotate-180",
              )}
              aria-hidden
            />
          </button>
          <AnimatePresence>
            {showSectionPicker && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute z-30 inset-x-0 mt-1 rounded-xl bg-white border border-zinc-200 shadow-xl shadow-zinc-900/[0.08] max-h-[260px] overflow-y-auto"
              >
                <ul className="py-1.5">
                  {ALL_SECTIONS.filter((s) => !usedSections.has(s)).map(
                    (s) => (
                      <li key={s}>
                        <button
                          type="button"
                          onClick={() => addNewSection(s)}
                          className="w-full text-left px-4 py-2.5 text-[13px] text-zinc-700 hover:bg-emerald-50 hover:text-emerald-900 transition-colors duration-100"
                        >
                          {sectionLabel(s)}
                        </button>
                      </li>
                    ),
                  )}
                  {ALL_SECTIONS.filter((s) => !usedSections.has(s)).length ===
                    0 && (
                    <li className="px-4 py-3 text-[12.5px] text-zinc-400">
                      All sections covered.
                    </li>
                  )}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* CTA bar */}
      <div
        className={cx(
          "space-y-2",
          "fixed inset-x-0 bottom-0 z-30",
          "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),12px)]",
          "bg-gradient-to-t from-white via-white/95 to-white/0",
          "sm:static sm:px-0 sm:pt-2 sm:pb-0 sm:bg-transparent",
        )}
      >
        <button
          type="button"
          onClick={handleSaveAndContinue}
          disabled={saving || totalRows === 0}
          className={cx(
            "w-full h-14 rounded-2xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-all duration-150",
            saving || totalRows === 0
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : "bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15",
          )}
        >
          {saving ? FE_DAMAGE_COPY.saving : FE_DAMAGE_COPY.continue}
          {!saving && <ArrowRight className="size-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          {FE_DAMAGE_COPY.back}
        </button>
      </div>

      <ConfirmDialog
        open={sectionPendingRemoval !== null}
        title="Remove this section?"
        body={
          sectionPendingRemoval
            ? `All components in "${sectionLabel(sectionPendingRemoval)}" will be removed from the estimate.`
            : ""
        }
        confirmLabel="Remove"
        cancelLabel="Keep"
        destructive
        onCancel={() => setSectionPendingRemoval(null)}
        onConfirm={confirmRemoveSection}
      />

      <ConfirmDialog
        open={rowPendingRemoval !== null}
        title="Delete this component?"
        body={
          rowPendingRemoval
            ? `"${rowPendingRemoval.label}" will be removed from the estimate. This can't be undone.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Keep"
        destructive
        onCancel={() => setRowPendingRemoval(null)}
        onConfirm={confirmRemoveRow}
      />
    </div>
  )
}

/* ── Component row ────────────────────────────────────────────── */

function ComponentRow({
  row,
  isEditing,
  onOpen,
  onClose,
  onUpdate,
  onDelete,
}: {
  row: RowState
  isEditing: boolean
  onOpen: () => void
  onClose: () => void
  onUpdate: (patch: Partial<RowState>) => void
  onDelete: () => void
}) {
  const overridden =
    row.ai !== null &&
    (row.action !== row.ai.action || row.partType !== row.ai.partType)
  const isNewRow = row.id === null
  const containerRef = useRef<HTMLDivElement>(null)

  // When a brand-new row enters edit mode (i.e. user just clicked
  // "+ Add component"), scroll it into view with breathing room
  // above so the section title stays visible. For existing rows the
  // user clicked, do nothing — they already know where they are.
  useEffect(() => {
    if (isEditing && isNewRow && containerRef.current) {
      // Defer to after the accordion height transition begins so we
      // scroll to the *expanded* position, not the collapsed one.
      const t = window.setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 60)
      return () => window.clearTimeout(t)
    }
  }, [isEditing, isNewRow])

  // Picker shows only components for this section. If the row's
  // current value isn't in the curated section list (e.g. a BE
  // value we don't have a label for), surface it so the select
  // can still render the current state.
  const sectionComponents = COMPONENT_OPTIONS.filter(
    (c) => c.section === row.section,
  )
  const currentInList = sectionComponents.some(
    (c) => c.value === row.component,
  )

  return (
    <div
      ref={containerRef}
      className={cx(
        "transition-colors duration-150",
        isEditing && "bg-zinc-50/60 ring-1 ring-inset ring-emerald-200/40",
      )}
    >
      {/* Header row — always visible */}
      <button
        type="button"
        onClick={isEditing ? onClose : onOpen}
        className="w-full text-left px-4 md:px-5 py-3 flex items-start gap-3 focus:outline-none focus-visible:bg-zinc-50"
      >
        <div className="flex-1 min-w-0">
          <p
            className={cx(
              "text-[14px] font-semibold leading-tight truncate",
              isNewRow ? "text-zinc-400 italic" : "text-zinc-900",
            )}
          >
            {isNewRow
              ? "New component — pick one below"
              : componentLabel(row.component)}
          </p>
          <div className="mt-1.5 flex items-center flex-wrap gap-x-2 gap-y-1">
            <span className="text-[11.5px] font-semibold text-zinc-700">
              {row.action === "REPLACE" ? "Replace" : "Repair"}
              <span className="text-zinc-300 mx-1">·</span>
              {row.partType}
            </span>
            {overridden && <EditedBadge />}
            <ConfidenceFlag level={row.confidence} />
          </div>
        </div>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 mt-1 transition-transform duration-200",
            isEditing && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {/* Edit accordion */}
      <AnimatePresence initial={false}>
        {isEditing && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 md:px-5 pb-4 pt-1 space-y-4">
              {/* Component picker — first field. Section-only;
                  no "other components" fallback so the list stays
                  short and matches the section card the user is
                  in. If they want a component from another
                  section, they add it to that section instead. */}
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                  Component
                </label>
                <Dropdown<VehicleComponent>
                  ariaLabel="Component"
                  value={row.component}
                  options={sectionComponents.map((c) => ({
                    value: c.value,
                    label: c.label,
                  }))}
                  onChange={(v) => onUpdate({ component: v })}
                  currentLabelFallback={
                    !currentInList ? componentLabel(row.component) : undefined
                  }
                  placeholder={
                    sectionComponents.length === 0
                      ? "No components for this section"
                      : "Pick a component"
                  }
                />
              </div>

              {/* Action + Part type — side-by-side on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                    {FE_DAMAGE_COPY.actionLabel}
                  </label>
                  <SegmentedToggle
                    options={ACTION_OPTIONS}
                    value={row.action}
                    onChange={(v) => onUpdate({ action: v })}
                    ariaLabel="Action"
                  />
                  {row.ai && (
                    <SuggestionMicrocopy
                      current={row.action}
                      suggested={row.ai.action}
                      optionLabel={(v) =>
                        v === "REPLACE" ? "Replace" : "Repair"
                      }
                      onRevert={() =>
                        row.ai && onUpdate({ action: row.ai.action })
                      }
                    />
                  )}
                </div>
                <div>
                  <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                    {FE_DAMAGE_COPY.partTypeLabel}
                  </label>
                  <SegmentedToggle
                    options={PART_TYPE_OPTIONS}
                    value={row.partType}
                    onChange={(v) => onUpdate({ partType: v })}
                    ariaLabel="Part type"
                  />
                  {row.ai && (
                    <SuggestionMicrocopy
                      current={row.partType}
                      suggested={row.ai.partType}
                      optionLabel={(v) => v}
                      onRevert={() =>
                        row.ai && onUpdate({ partType: row.ai.partType })
                      }
                    />
                  )}
                </div>
              </div>

              {/* Side */}
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                  {FE_DAMAGE_COPY.sideLabel}
                </label>
                <div className="grid grid-cols-5 gap-1.5">
                  {SIDE_OPTIONS.map((opt) => {
                    const selected = opt.value === row.side
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onUpdate({ side: opt.value })}
                        className={cx(
                          "h-9 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors duration-150",
                          selected
                            ? "bg-emerald-500 text-white shadow-sm"
                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
                        )}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Action row */}
              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                <button
                  type="button"
                  onClick={onDelete}
                  className="h-10 px-3.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-[12.5px] font-semibold transition-colors duration-150"
                >
                  {FE_DAMAGE_COPY.deleteRow}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 px-4 rounded-lg bg-zinc-900 hover:bg-black text-white text-[12.5px] font-semibold sm:ml-auto transition-colors duration-150"
                >
                  {FE_DAMAGE_COPY.doneEditing}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

